import { NextPage } from 'next';
import Router from 'next/router';

/**
 * Higher-Order Component (HOC) factory that creates a Next.js page component
 * that redirects to the specified path.
 *
 * Handles both server-side (SSR) and client-side redirects:
 * - Server-side: Uses HTTP 302 redirect via response headers
 * - Client-side: Uses Next.js Router.replace() for client-side navigation
 *
 * The returned component renders nothing (returns null) as it immediately redirects.
 *
 * @param path - The target path to redirect to (e.g., '/login', '/dashboard')
 * @returns A Next.js page component that performs the redirect
 *
 * @example
 * ```ts
 * // In a Next.js page file (pages/index.tsx)
 * import { redirectTo } from '@elevator/ui';
 *
 * export default redirectTo('/login');
 * ```
 */
export const redirectTo = (path: string): NextPage => {
  const Redirect: NextPage = () => {
    return null;
  };

  Redirect.getInitialProps = ({ res }) => {
    if (res) {
      // Server-side redirect using HTTP 302 (Temporary Redirect)
      res.writeHead(302, {
        Location: path,
      });
      res.end();
    } else {
      // Client-side redirect using Next.js Router
      Router.replace(path);
    }

    return {};
  };

  return Redirect;
};
